import { useEffect, useRef, useState } from 'react';
import { ArrowRight, ArrowLeft } from '../../assets/svgImages';
import { Product, GuestSimilarProductsProps } from '../../interface/interface';
import { calculateOutOfStockEnum } from '../../utils/outOfStockStatus';

import { useGuestCart } from '../../context/guestCartContext';
import GuestProductCards from './GuestProductCards';

const GuestSimilarProducts = ({ products }: GuestSimilarProductsProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const {
    getProductQuantity,
    addToCartMutation,
    removeProductFromCartMutation,
  } = useGuestCart();

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -240 : 240,
      behavior: 'smooth',
    });
  };

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadingState =
      loading ||
      addToCartMutation.isPending ||
      removeProductFromCartMutation.isPending;

    if (!loadingState) {
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [
    loading,
    addToCartMutation.isPending,
    removeProductFromCartMutation.isPending,
  ]);

  function frameProductObject(product: Product) {
    const orderedQuantity = getProductQuantity(product?.product_code) ?? 0;

    if (product?.substituteProduct) {
      const subProduct = product?.substituteProduct;
      const subOrderedQuantity =
        getProductQuantity(subProduct?.product_code) ?? 0;

      return {
        ...product,
        substituteProduct: {
          ...subProduct,
          productMaxQty: product?.productMaxQty ?? product.inventory_quantity,
          orderedQuantity: subOrderedQuantity,
          outOfStock: calculateOutOfStockEnum(subProduct, orderedQuantity),
        },
        productMaxQty: product?.productMaxQty ?? product.inventory_quantity,
        orderedQuantity,
        outOfStock: calculateOutOfStockEnum(product, orderedQuantity),
      };
    }

    return {
      ...product,
      orderedQuantity,
      outOfStock: calculateOutOfStockEnum(product, orderedQuantity),
    };
  }

  if (!products?.length) return null;

  return (
    <div>
      <h2 className="text-lg md:text-xl xl:text-2xl font-semibold text-primary-blue-500 mb-4 lg:mb-5">
        You may also like
      </h2>
      <div className="relative">
        <div
          ref={scrollRef}
          className="grid gap-4 xs:grid-cols-1 sm:grid-cols-2 sm-land:grid-cols-3 lg:grid-cols-4"
        >
          {products.map((product) => (
            <GuestProductCards
              key={product.id}
              product={frameProductObject(product)}
              addProductToCart={async (id: string) => {
                await addToCartMutation.mutateAsync(id);
              }}
              removeProductFromCart={async (productId: string) => {
                await removeProductFromCartMutation.mutateAsync(productId);
              }}
              isAccessories={false}
              allowedProduct={false}
              isGuest={true}
            />
          ))}
        </div>
        <div
          className={`${products?.length > 2 ? 'sm:block md:hidden' : ''} ${products?.length > 5 ? 'block' : 'md:hidden'}`}
        >
          <button
            type="button"
            onClick={() => scroll('left')}
            className="hidden sm:flex absolute -left-4 lg:-left-6 top-1/2 -translate-y-1/2 bg-white border border-gray-200
            rounded-full w-8 h-8 items-center justify-center shadow hover:bg-gray-50
            transition-colors z-10"
            aria-label="Scroll left"
          >
            <ArrowRight />
          </button>
          <button
            type="button"
            onClick={() => scroll('right')}
            className="hidden sm:flex absolute -right-4 lg:-right-6 top-1/2 -translate-y-1/2 bg-white border border-gray-200
            rounded-full w-8 h-8 items-center justify-center shadow hover:bg-gray-50
            transition-colors z-10"
            aria-label="Scroll right"
          >
            <ArrowLeft />
          </button>
        </div>
      </div>
    </div>
  );
};

export default GuestSimilarProducts;
